// @file Vue component mixin that allow our VueJS padlet surface to work on the mobile app
// through postMessage and JS callbacks executed by WebView programmatically.
/* eslint-disable class-methods-use-this */
import { isAppUsing } from '@@/bits/flip'
import { isPostScheduled } from '@@/bits/post_state'
import { setSafeAreaInsets } from '@@/bits/safe_area_insets'
import { getPollFromPost, isPollClosed } from '@@/bits/surface_polls'
import type { EdgeInsets } from '@@/bits/window'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { NativeAppOpenLink } from '@@/vuexstore/modules/native_app'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import appCan, { isAppCapableOf } from 'bits/app_can'
import { getStaticAttachmentType, isAttachmentDownloadable, StaticAttachmentType } from 'bits/attachments'
import { DOMContentLoadedPromise } from 'bits/dom'
import window from 'bits/global'
import { buildUrlFromPath, currentPath, navigateTo } from 'bits/location'
import { Action, Component, Getter, namespace, State, Vue, Watch } from 'bits/vue'
import {
  convertLayoutResult,
  editPost,
  expandPost,
  firstTouch,
  openLink,
  openMapLocationPicker,
  pageReady,
  postFilterState,
  reportCopyPostResult,
  reportTransferPostResult,
  saveSettingsResult,
  showCommentsPanel,
  updateActiveContributorsList,
  updateAddress,
  updateBackground,
} from 'native_bridge/actions'
import { createClickListener } from 'native_bridge/anchor'
import { listen } from 'native_bridge/listen'
import { showNativePostActionMenu } from 'native_bridge/post_action_menu'
import postMessage from 'native_bridge/post_message'
import type { ActionMenuWallData, ListenedMessage, ListenedMessageHandler } from 'native_bridge/types'
import {
  createCommentMenuAlertSheet,
  createCopyPostSidePanel,
  createPostActionAlertSheet,
  createSectionMenuAlertSheet,
  createTransferPostSidePanel,
  PostActionMenuItem,
  showNativeUI,
} from 'native_bridge/ui'
import { useSurfaceFilterStore } from 'pinia/surface_filter'
import { useSurfacePostSearchStore } from 'pinia/surface_post_search'
import * as sorter from 'surface/sorter'
import type { Cid, DraftPost, PostColor, User } from 'types'
import { Post } from 'types'
import type { FilterGroup } from 'types/surface'
import { PostActionType, VALID_COLORS } from 'vuexstore/helpers/post'
import { CopyOrTransferPostResult } from 'vuexstore/surface/types'

type CopyOrTransferType = 'new' | 'current' | 'other'

const comment = namespace('comment')
const section = namespace('section')
const nativeApp = namespace('nativeApp')
const settings = namespace('settings')
const post = namespace('post')
const postAction = namespace('postAction')
const postConnection = namespace('postConnection')
const map = namespace('map')
const contributingStatus = namespace('contributingStatus')
const attachmentModule = namespace('attachment')
const draft = namespace('draft')

@Component({})
class NativeAppMixin extends Vue {
  @State constants
  @State xCommentsPanel
  @State commentsPanelPostId
  @State wall
  @State user
  @State isIOS
  @State isAndroid
  @Getter isSlideshow
  @Getter address
  @Getter background
  @Getter convertLayoutErrorMessage
  @Getter format
  @Getter isMap
  @Getter links
  @Getter publicKey
  @Action refresh
  @Action setCoverPost
  @Action unsetCoverPost
  @Action hideCommentsPanel

  @comment.State actionMenuCommentId
  @comment.Action startEditingComment
  @comment.Action deleteComment

  @section.State actionMenuSectionId
  @section.Action startEditingSection
  @section.Action moveSectionLeft
  @section.Action moveSectionRight
  @section.Action deleteSection

  @settings.Getter isSaving
  @settings.Getter saveErrorMessage

  @map.Getter isPickingLocation
  @map.Action startPickingLocation
  @map.Action stopPickingLocation
  @map.Action pickLocation

  @nativeApp.State public linkToOpen
  @nativeApp.State alertSheetManifest
  @nativeApp.State sidePanelManifest
  @nativeApp.Getter expandedPost
  @nativeApp.Action clearOpenLink
  @nativeApp.Action hideAllPanelsAndMenus
  @nativeApp.Action updateShelfSectionBottomPadding
  @nativeApp.Action postSurfaceState!: () => void

  @post.Getter savedPostsArray
  @post.Getter postBeingEdited
  @post.Getter postEntitiesById
  @post.Action startEditingPost
  @post.Action deletePost
  @post.Action sortedPost
  @post.Action removePostColor
  @post.Action changePostColor

  @postAction.State copyOrTransferPostResult
  @postAction.Getter postUnderAction
  @postAction.Getter canEditPostUnderAction!: boolean
  @postAction.Getter canConnectPostUnderAction
  @postAction.Getter canDisconnectPostUnderAction
  @postAction.Getter canChangePostStackOrder
  @postAction.Getter canTransferPostUnderAction
  @postAction.Getter canCopyPostUnderAction
  @postAction.Getter canChangeCoverToPostUnderAction
  @postAction.Getter canChangePostUnderActionLocation
  @postAction.Getter isPostUnderActionPreviewPost
  @postAction.Getter canDeletePostUnderAction
  @postAction.Getter canOpenInMapApp!: boolean
  @postAction.Action expandPost!: (args: { postCid: Cid }) => void
  @postAction.Action showPostActionMenu!: (args: { postCid: Cid }) => void
  @postAction.Action startPostTransfer
  @postAction.Action startPostCopy
  @postAction.Action hidePostActionMenu
  @postAction.Action unexpandPost!: () => void
  @postAction.Action closePoll!: ({ post }) => Promise<void>
  @postAction.Action reopenPoll!: ({ post }) => Promise<void>

  @postConnection.Action startConnection
  @postConnection.Action startDisconnection

  @attachmentModule.Getter linkDisplayAttributesForPost!: (postCid: Cid) => BeethovenDisplayAttributes | null
  @attachmentModule.Action fetchDisplayAttributesForPost!: (postCid: Cid) => Promise<void>

  @draft.Getter activeDraft!: DraftPost | null

  @contributingStatus.Getter activeContributorsList
  @Watch('address')
  public respondToAddressChange(newValue: string, oldValue: string): void {
    postMessage(updateAddress(newValue))
  }

  @Watch('linkToOpen')
  public respondToLinkToOpenChange(newValue: NativeAppOpenLink | null, oldValue: NativeAppOpenLink | null): void {
    if (newValue != null && oldValue == null) {
      const { linkType, url, contentCategory, contentSubcategory, username } = newValue
      postMessage(openLink({ url, linkType, contentCategory, contentSubcategory, username }))
      this.clearOpenLink()
    }
  }

  @Watch('expandedPost')
  respondToSettingExpandedPostManifest(newExpandedPost): void {
    if (!newExpandedPost) return
    const { wishFrameUrlTemplate } = this.constants
    postMessage(
      expandPost({
        expandedPost: newExpandedPost,
        allPosts: this.savedPostsArray,
        wall: this.wall,
        window,
        wishFrameUrlTemplate,
      }),
    )
    this.postSurfaceState()
  }

  @Watch('format')
  public respondToLayoutChange(): void {
    postMessage(convertLayoutResult({ status: 'success' }))
  }

  @Watch('convertLayoutErrorMessage')
  public respondToLayoutChangeError(errorMessage): void {
    if (errorMessage) {
      postMessage(
        convertLayoutResult({
          status: 'error',
          error_reason: errorMessage,
        }),
      )
    }
  }

  @Watch('background')
  public respondToBackgroundColorChange(newValue): void {
    postMessage(updateBackground(newValue))
  }

  @Watch('actionMenuCommentId')
  respondToActionMenuCommentId(actionMenuCommentId: number | null): void {
    if (!actionMenuCommentId) return
    this.listenToCommentActionMenu(actionMenuCommentId)
    showNativeUI(createCommentMenuAlertSheet(actionMenuCommentId))
  }

  listenToCommentActionMenu(commentUnderActionId: number): void {
    listen('edit_comment', (): void => {
      if (isAppUsing('surfaceCommentsV2')) {
        useCommentsStore().startEditingCommentV2({ commentId: commentUnderActionId })
      } else {
        this.startEditingComment({ commentId: commentUnderActionId })
      }
    })
    listen('delete_comment', (): void => {
      this.deleteComment({ commentId: commentUnderActionId })
    })
  }

  @Watch('actionMenuSectionId')
  respondToActionMenuSectionId(actionMenuSectionId: number | null): void {
    if (!actionMenuSectionId) return
    this.listenToSectionActionMenu(actionMenuSectionId)
    showNativeUI(createSectionMenuAlertSheet(actionMenuSectionId))
  }

  listenToSectionActionMenu(sectionUnderActionId: number): void {
    listen('edit_section', (): void => {
      this.startEditingSection({ sectionId: sectionUnderActionId })
    })
    listen('move_section_left', (): void => {
      this.moveSectionLeft({ sectionId: sectionUnderActionId })
    })
    listen('move_section_right', (): void => {
      this.moveSectionRight({ sectionId: sectionUnderActionId })
    })
    listen('delete_section', (): void => {
      this.deleteSection({ sectionId: sectionUnderActionId })
    })
  }

  @Watch('activeDraft')
  respondToActiveDraftChange(currentDraft?: DraftPost | null, lastDraft?: DraftPost | null): void {
    if (currentDraft == null) return
    if (currentDraft?.cid !== lastDraft?.cid) {
      postMessage(editPost(currentDraft))
    }
  }

  @Watch('activeContributorsList')
  respondToActiveContributorsListChanged(currentList: Array<Partial<User>>, _oldList: Array<Partial<User>>): void {
    postMessage(updateActiveContributorsList(currentList))
  }

  @Watch('isPickingLocation')
  respondToIsPickingLocation(isPickingLocation: boolean): void {
    if (!appCan('useNativeMapLocationPicker')) return
    if (!isPickingLocation) return
    postMessage(openMapLocationPicker())
    listen('map_location_picked', async ({ location, name }): Promise<void> => {
      if (location && name) {
        const LatLng = (await import('bits/map')).LatLng
        this.pickLocation({ latLng: new LatLng(location), name })
      }
      this.stopPickingLocation()
    })
  }

  @Watch('isSaving')
  public respondToSettingsSave(isSaving: boolean, wasSaving: boolean): void {
    const justSaved = !isSaving && wasSaving
    if (!justSaved) return

    let result: { [key: string]: unknown } = {}
    if (this.saveErrorMessage) {
      result = {
        status: 'error',
        error_reason: this.saveErrorMessage,
      }
    } else {
      result = {
        status: 'success',
        wall: this.wall,
      }
    }
    postMessage(saveSettingsResult(result))
  }

  @Watch('postUnderAction')
  respondToPostUnderAction(postUnderAction: Post): void {
    if (!postUnderAction) return

    // 1. Listen to events
    this.listenToPostUnderActionMenu(postUnderAction)

    // 2. create the postUnderAction action menu manifest here and show it
    if (appCan('nativePostActionMenuV2')) {
      this.showPostActionMenuV2(postUnderAction)
    } else {
      this.showPostActionMenuV1(postUnderAction)
    }
  }

  get actionMenuWallData(): ActionMenuWallData {
    return {
      publicKey: this.wall.public_key,
      links: this.links,
      themeColors: VALID_COLORS,
      colorScheme: this.wall.color_scheme,
      mobileAppCopyPostUrlTemplate: this.constants.mobileAppCopyPostUrlTemplate,
      mobileAppTransferPostUrlTemplate: this.constants.mobileAppTransferPostUrlTemplate,
    }
  }

  showPostActionMenuV1(postUnderAction: Post): void {
    // Specify which options will appear in the alert sheet.
    const sheetOptions: PostActionMenuItem[] = []
    if (this.canEditPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Colors)
      sheetOptions.push(PostActionMenuItem.Edit)
    }
    if (this.canChangePostUnderActionLocation) {
      sheetOptions.push(PostActionMenuItem.ChangeLocation)
    }
    if (this.canConnectPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Connect)
    }
    if (this.canDisconnectPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Disconnect)
    }
    if (this.canChangePostStackOrder) {
      sheetOptions.push(PostActionMenuItem.BringPostToFront)
      sheetOptions.push(PostActionMenuItem.SendPostToBack)
    }
    if (this.canEditPostUnderAction || this.canConnectPostUnderAction || this.canDisconnectPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Divider)
    }
    if (this.canTransferPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Transfer)
    }
    if (this.canCopyPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Copy)
    }
    if (!this.expandedPost) {
      sheetOptions.push(PostActionMenuItem.Expand)
    }
    if (this.canChangeCoverToPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Divider)
      if (this.isPostUnderActionPreviewPost) {
        sheetOptions.push(PostActionMenuItem.UnsetAsCover)
      } else {
        sheetOptions.push(PostActionMenuItem.SetAsCover)
      }
    }
    if (this.canDeletePostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Divider)
      sheetOptions.push(PostActionMenuItem.Delete)
    }

    const { id: postId, cid: postCid, author_id: postAuthorId } = postUnderAction
    const id = Number(postId)
    const cid = postCid
    const author_id = Number(postAuthorId)

    const alertSheet = createPostActionAlertSheet({ id, cid, author_id }, this.actionMenuWallData, sheetOptions)
    showNativeUI(alertSheet)
  }

  async showPostActionMenuV2(postUnderAction: Post): Promise<void> {
    if (!postUnderAction) return
    const isPostUnderActionExpanded = postUnderAction.id === this.expandedPost?.id

    let isPostAttachmentDownloadable = false
    let postStaticAttachmentType: StaticAttachmentType = StaticAttachmentType.Unknown

    // We only care about where the attachment is downloadable or not for expanded post view
    if (isPostUnderActionExpanded) {
      if (this.linkDisplayAttributesForPost(postUnderAction.cid) == null) {
        await this.fetchDisplayAttributesForPost(postUnderAction.cid)
      }
      const attachmentDisplayAttributes = this.linkDisplayAttributesForPost(postUnderAction.cid)
      isPostAttachmentDownloadable =
        !!postUnderAction.attachment && isAttachmentDownloadable(attachmentDisplayAttributes)
      postStaticAttachmentType = getStaticAttachmentType(attachmentDisplayAttributes)
    }

    const group0: PostActionType[] = []
    const group1: PostActionType[] = []
    const group2: PostActionType[] = []
    const group3: PostActionType[] = []
    const group4: PostActionType[] = []
    const group5: PostActionType[] = []

    // GROUP 0 - Poll specific, to always be replaced before the last group
    const poll = getPollFromPost(postUnderAction)
    if (poll != null && this.canEditPostUnderAction) {
      group0.push(isPollClosed(poll) ? PostActionType.OpenPoll : PostActionType.ClosePoll)
      group0.push(PostActionType.ExportPollResultsCSV)
    }

    // Build the action list
    if (isPostUnderActionExpanded) {
      // should match the logic in SurfacePanelPostExpandedActionMenu.vue with some slight differences.
      // Detailed Figma: https://www.figma.com/file/SmrcMyYT7uv83MXTsypoho/Post-Expanded-View-v1---ENG?node-id=8494%3A114599

      // GROUP 1
      if (appCan('nativePostActionMenuWithSharing')) {
        group1.push(PostActionType.SharePostLink)
      } else {
        group1.push(PostActionType.CopyPostLink)
      }
      if (appCan('useNativeSlideshowWebview') && !isPostScheduled(postUnderAction)) {
        group1.push(PostActionType.StartSlideshowFromThisPost)
      }
      if (isAppCapableOf('appOpenInMap') && this.canOpenInMapApp) {
        group1.push(PostActionType.OpenLocationInMapApp)
      }

      // GROUP 2
      if (postUnderAction.attachment) {
        if (appCan('nativePostActionMenuWithSharing')) {
          if (isPostAttachmentDownloadable) {
            group2.push(PostActionType.ShareStaticAttachment)
          }
          group2.push(PostActionType.ShareAttachmentLink)
        } else {
          group2.push(PostActionType.CopyAttachmentLink)
        }
      }

      // GROUP 3
      if (this.canEditPostUnderAction) {
        group3.push(PostActionType.EditPost)
      }

      // GROUP 4
      if (!isPostScheduled(postUnderAction)) {
        if (this.canCopyPostUnderAction) {
          group4.push(PostActionType.DuplicatePost)
        }
        if (this.canTransferPostUnderAction) {
          group4.push(PostActionType.TransferPost)
        }
      }
      if (this.canChangeCoverToPostUnderAction && this.isPostUnderActionPreviewPost) {
        group4.push(PostActionType.UnsetAsPadletCover)
      }
      if (this.canChangeCoverToPostUnderAction && !this.isPostUnderActionPreviewPost) {
        group4.push(PostActionType.SetAsPadletCover)
      }

      // GROUP 5
      if (appCan('surfaceAbuseReportPanel') && !isPostScheduled(postUnderAction)) {
        group5.push(PostActionType.ReportPost)
      }
      if (this.canDeletePostUnderAction) {
        group5.push(PostActionType.DeletePost)
      }
    } else {
      // should match the logic in SurfacePanelPostActionMenu.vue with some slight differences.
      // Detailed Figma: https://www.figma.com/file/SmrcMyYT7uv83MXTsypoho/Post-Expanded-View-v1---ENG?node-id=8494%3A114599

      // GROUP 1
      group1.push(PostActionType.ExpandPost, PostActionType.CopyPostLink)

      if (appCan('useNativeSlideshowWebview') && !isPostScheduled(postUnderAction)) {
        group1.push(PostActionType.StartSlideshowFromThisPost)
      }
      if (isAppCapableOf('appOpenInMap') && this.canOpenInMapApp) {
        group1.push(PostActionType.OpenLocationInMapApp)
      }

      // GROUP 2
      if (this.canEditPostUnderAction) {
        group2.push(PostActionType.ChangePostColor)
        group2.push(PostActionType.EditPost)
      }
      if (this.canConnectPostUnderAction) {
        group2.push(PostActionType.ConnectToPost)
      }
      if (this.canDisconnectPostUnderAction) {
        group2.push(PostActionType.DisconnectFromPost)
      }
      if (this.canChangePostStackOrder) {
        group2.push(PostActionType.BringToFront)
        group2.push(PostActionType.SendToBack)
      }

      // GROUP 3
      if (!isPostScheduled(postUnderAction)) {
        if (this.canCopyPostUnderAction) {
          group3.push(PostActionType.DuplicatePost)
        }
        if (this.canTransferPostUnderAction) {
          group3.push(PostActionType.TransferPost)
        }
      }
      if (this.canChangeCoverToPostUnderAction && this.isPostUnderActionPreviewPost) {
        group3.push(PostActionType.UnsetAsPadletCover)
      }
      if (this.canChangeCoverToPostUnderAction && !this.isPostUnderActionPreviewPost) {
        group3.push(PostActionType.SetAsPadletCover)
      }

      // GROUP 4
      if (appCan('surfaceAbuseReportPanel') && !isPostScheduled(postUnderAction)) {
        group4.push(PostActionType.ReportPost)
      }
      if (this.canDeletePostUnderAction) {
        group4.push(PostActionType.DeletePost)
      }
    }

    const desiredActionGroups = (
      isPostUnderActionExpanded
        ? [group1, group2, group3, group4, group0, group5]
        : [group1, group2, group3, group0, group4]
    ).filter((group) => group.length > 0)

    showNativePostActionMenu({
      post: postUnderAction,
      wall: this.actionMenuWallData,
      desiredActionGroups,
      isPostUnderActionExpanded,
      isPostAttachmentDownloadable,
      postStaticAttachmentType,
    })
  }

  listenToPostUnderActionMenu(postUnderAction: Post): void {
    const postId = Number(postUnderAction.id)
    const postCid = postUnderAction.cid
    const authorId = Number(postUnderAction.author_id)
    const hideMenuFirst = (wrapped: Function): ListenedMessageHandler => {
      return (msg: unknown): void => {
        this.hidePostActionMenu()
        wrapped(msg)
      }
    }
    listen(
      'edit_post',
      hideMenuFirst((): void => {
        this.startEditingPost({ postCid })
      }),
    )
    listen(
      'connect_post',
      hideMenuFirst((): void => {
        this.startConnection({ postId })
      }),
    )
    listen(
      'disconnect_post',
      hideMenuFirst((): void => {
        this.startDisconnection({ postId })
      }),
    )
    listen(
      'bring_post_to_front',
      hideMenuFirst((): void => {
        this.sortedPost({ postCid, sortIndex: sorter.getTopSortIndex() })
      }),
    )
    listen(
      'send_post_to_back',
      hideMenuFirst((): void => {
        this.sortedPost({ postCid, sortIndex: sorter.getBottomSortIndex() })
      }),
    )
    listen(
      'expand_post',
      hideMenuFirst((): void => {
        this.expandPost({ postCid })
      }),
    )
    listen(
      'delete_post',
      hideMenuFirst((): void => {
        this.deletePost({ postCid })
      }),
    )
    listen(
      'update_post_color',
      hideMenuFirst((msg: ListenedMessage & { label: PostColor }): void => {
        this.changePostColor({ postCid, newBgColor: msg.label })
      }),
    )
    listen(
      'remove_post_color',
      hideMenuFirst((): void => {
        this.removePostColor({ postCid })
      }),
    )
    listen(
      'set_as_cover',
      hideMenuFirst((): void => {
        this.setCoverPost({ postId })
      }),
    )
    listen(
      'unset_as_cover',
      hideMenuFirst((): void => {
        this.unsetCoverPost({ postId })
      }),
    )
    listen(
      'change_location',
      hideMenuFirst((): void => {
        this.startPickingLocation({ postCid })
      }),
    )
    listen(
      'transfer_post',
      hideMenuFirst((): void => {
        // track TODO
        showNativeUI(
          createTransferPostSidePanel(this.constants.mobileAppTransferPostUrlTemplate, this.wall, {
            id: postId,
            author_id: authorId,
          }),
        )
        this.startPostTransfer({ transferPostCid: postCid })
      }),
    )
    listen('copy_post', (): void => {
      // track TODO
      showNativeUI(
        createCopyPostSidePanel(this.constants.mobileAppCopyPostUrlTemplate, this.wall, {
          id: postId,
          author_id: authorId,
        }),
      )
      this.startPostCopy({ copyPostCid: postCid })
    })

    listen(
      'close_poll',
      hideMenuFirst((): void => {
        void this.closePoll({ post: postUnderAction })
      }),
    )

    listen(
      'open_poll',
      hideMenuFirst((): void => {
        void this.reopenPoll({ post: postUnderAction })
      }),
    )
  }

  @Watch('copyOrTransferPostResult')
  respondToCopyingOrTransferringPost(copyOrTransferPostResult: CopyOrTransferPostResult): void {
    if (!copyOrTransferPostResult) return
    const { isDestinationNewWall, isDestinationThisWall, status, url, text, operation } = copyOrTransferPostResult
    let actionType: CopyOrTransferType = 'other'
    if (isDestinationNewWall) {
      actionType = 'new'
    } else if (isDestinationThisWall) {
      actionType = 'current'
    }
    if (operation === 'copy') {
      postMessage(
        reportCopyPostResult({
          copy_type: actionType,
          status,
          url,
          text,
        }),
      )
    } else if (operation === 'transfer') {
      postMessage(
        reportTransferPostResult({
          transfer_type: actionType,
          status,
          url,
          text,
        }),
      )
    } else {
      throw new Error(`Unknown operation: ${operation}`)
    }
  }

  @Watch('xCommentsPanel')
  respondToShowCommentsPanel(newXCommentsPanel: boolean): void {
    // older versions of the app should still show the web based comments panel
    if (!appCan('useNativeCommentsPanel')) {
      return
    }

    if (newXCommentsPanel) {
      postMessage(showCommentsPanel(this.commentsPanelPostId))
      this.hideCommentsPanel()
    }
  }

  postMessageOnDomReady(): void {
    if (!(this.isIOS || this.isAndroid)) return
    if (currentPath().includes('beethoven/attach') || currentPath().includes('mobile-app/attach')) return

    const isDisplayedInIFrame = window.self !== window.top
    if (isDisplayedInIFrame) return

    if (this.isSlideshow) {
      // if we're in a slideshow, we don't want to redirect the user to a wall not found page since it is not a wall
      return
    }
    postMessage(pageReady(), () => navigateTo(buildUrlFromPath('/ios-app/page-ready')))
    // post the initial surface state
    this.postSurfaceState()
  }

  @namespace('userContributor').Action fetchPostAuthors!: () => Promise<void>

  created(): void {
    void this.fetchPostAuthors()
  }

  mounted(): void {
    // Make all body anchor tags listen to click events and
    // respond to native app
    document.body.addEventListener('click', createClickListener(this.$store))
    this.listenForFirstTouch()
    this.installGlobalListeners()
    this.installSearchAndFilterPostsListeners()
    this.installMentionListeners()
    this.installExpandedPostListeners()
    this.installDraftUpdateListeners()
    void DOMContentLoadedPromise.then(this.postMessageOnDomReady)
  }

  // Collapses header on first touch
  // Only collapse if the touch is in vertical direction
  // After that, no need to listen for touch events anymore
  // since the header is already collapsed
  listenForFirstTouch(): void {
    if (!appCan('useNativeHeader')) return

    let initialTouchX = 0
    let initialTouchY = 0
    const TOUCH_THRESHOLD = 10 // pixels

    const handleTouchStart = (e: TouchEvent): void => {
      initialTouchX = e.touches[0].clientX
      initialTouchY = e.touches[0].clientY
    }

    const handleTouchEnd = (e: TouchEvent): void => {
      const finalTouchX = e.changedTouches[0].clientX
      const finalTouchY = e.changedTouches[0].clientY
      const deltaX = Math.abs(finalTouchX - initialTouchX)
      const deltaY = Math.abs(finalTouchY - initialTouchY)
      const isVerticalTouch = deltaY / deltaX > 0 && deltaY > TOUCH_THRESHOLD
      if (isVerticalTouch) {
        postMessage(firstTouch({ isVerticalTouch }))
        document.body.removeEventListener('touchstart', handleTouchStart)
        document.body.removeEventListener('touchend', handleTouchEnd)
      }
    }

    document.body.addEventListener('touchstart', handleTouchStart)
    document.body.addEventListener('touchend', handleTouchEnd)
  }

  installGlobalListeners(): void {
    listen('refresh_padlet', this.refresh)
    listen('cancel_show', this.hideAllPanelsAndMenus)
    listen('update_shelf_section_bottom_padding', this.updateShelfSectionBottomPadding)
    listen('post_surface_state', this.postSurfaceState)
    listen('set_safe_area_insets', (message: { message_type: string; safeAreaInsets: EdgeInsets }) => {
      setSafeAreaInsets(message.safeAreaInsets)
    })
  }

  installSearchAndFilterPostsListeners(): void {
    listen('update_search_term', (message: { message_type: 'update_search_term'; payload: { searchTerm: string } }) => {
      useSurfacePostSearchStore().setSearchTerm(message.payload.searchTerm)
    })
    listen('show_post_filters', () => {
      const filters = useSurfaceFilterStore().orderedFilterState
      postMessage(postFilterState(filters))
    })
    listen(
      'update_post_filter',
      (message: {
        message_type: 'update_post_filter'
        payload: { group: FilterGroup; key: string; value: boolean }
      }) => {
        const { group, key, value } = message.payload
        useSurfaceFilterStore().updatePostFilter(group, key, value)
      },
    )
    listen(
      'reset_post_filters',
      (message: { message_type: 'reset_post_filters'; payload: { group?: FilterGroup } }) => {
        useSurfaceFilterStore().resetPostFilters(message.payload.group)
      },
    )
  }

  installMentionListeners(): void {
    listen(
      'pick_mention_suggestion',
      (message: { message_type: 'pick_mention_suggestion'; payload: { type: 'user'; id: User['id'] } }) => {
        // TODO: insert a mention of user with id into the active editor
      },
    )
  }

  installExpandedPostListeners(): void {
    listen('hide_expanded_post', (): void => {
      this.unexpandPost()
      this.postSurfaceState()
    })
    // if the post has just been created by the mobile app's native post editor, postCid inside the webview will be c_new<number> instead of c<post_id>
    // => to be safe, find post by id first in set_expanded_post_id and show_post_action_menu
    listen('set_expanded_post_id', (message: ListenedMessage & { payload: { post_id: string } }): void => {
      const postCid = this.postEntitiesById[message.payload.post_id]?.cid
      if (postCid == null) {
        return
      }
      this.expandPost({ postCid })
      this.postSurfaceState()
    })
    listen('show_post_action_menu', (message: ListenedMessage & { payload: { post_id: string } }): void => {
      const postCid = this.postEntitiesById[message.payload.post_id]?.cid
      if (postCid == null) {
        return
      }
      this.showPostActionMenu({ postCid })
    })
    listen('hide_post_action_menu', (): void => {
      this.hidePostActionMenu()
    })
  }

  installDraftUpdateListeners(): void {
    listen('update_draft', (message: ListenedMessage & { payload: DraftPost }) => {
      useSurfaceDraftsStore().updateDraft(message.payload)
    })

    listen('remove_draft', (message: ListenedMessage & { payload: { cid: Cid } }) => {
      void useSurfaceDraftsStore().removeDraft(message.payload.cid)
    })

    listen('publish_all_drafts', (_message: ListenedMessage) => {
      void useSurfaceDraftsStore().publishAllPostDrafts()
    })

    listen('remove_all_drafts', (_message: ListenedMessage) => {
      void useSurfaceDraftsStore().removeAllPostDrafts()
    })

    listen('start_editing_draft', (message: ListenedMessage & { payload: { cid: Cid } }) => {
      void useSurfaceDraftsStore().startEditingDraft(message.payload.cid)
    })

    listen('stop_editing_draft', () => {
      void useSurfaceDraftsStore().stopEditingDraft()
    })

    listen('cancel_editing_draft', () => {
      void useSurfaceDraftsStore().cancelEditingDraft()
    })

    listen('save_post', (message: ListenedMessage & { payload: { attributes: Post } }) => {
      void useSurfacePostsStore().savePost({ attributes: message.payload.attributes })
    })
  }
}

export default NativeAppMixin
